import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { platzNebenStelle, type Rechteck } from "./platzNebenStelle";
import "./kommentare.css";
import { alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Ein Kasten NEBEN einer Stelle im Notizfeld, auf ihrer Höhe — Portal am
 * `body` mit `position: fixed`, damit ihn keine Karte mit `overflow: hidden`
 * abschneidet. Wohin genau, entscheidet `platzNebenStelle`.
 *
 * `stelle` liefert das Rechteck bei jedem Nachrechnen neu: beim Scrollen
 * wandert die Stelle, der `fixed` Kasten nicht.
 */
export const NebenStelle: React.FC<{
    stelle: () => Rechteck | null;
    feld: HTMLElement | null;
    breite: number;
    className?: string;
    children: React.ReactNode;
    onMouseDown?: (e: React.MouseEvent) => void;
    innerRef?: React.MutableRefObject<HTMLDivElement | null>;
    role?: string;
    ariaLabel?: string;
}> = ({ stelle, feld, breite, className, children, onMouseDown, innerRef, role, ariaLabel }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [platz, setPlatz] = useState<{ left: number; top: number } | null>(null);

    const stellen = useCallback(() => {
        const el = ref.current;
        const r = stelle();
        if (!el || !r) return;
        const f = feld?.getBoundingClientRect() ?? null;
        const p = platzNebenStelle({
            stelle: r,
            feld: f,
            breite,
            hoehe: el.offsetHeight || 160,
            fenster: { breite: aktivesFenster().innerWidth, hoehe: aktivesFenster().innerHeight },
        });
        setPlatz((alt) => (alt && alt.left === p.left && alt.top === p.top ? alt : { left: p.left, top: p.top }));
    }, [stelle, feld, breite]);

    useLayoutEffect(stellen);

    useEffect(() => {
        alleFenster.addEventListener("resize", stellen);
        alleFenster.addEventListener("scroll", stellen, true);
        return () => {
            alleFenster.removeEventListener("resize", stellen);
            alleFenster.removeEventListener("scroll", stellen, true);
        };
    }, [stellen]);

    return createPortal(
        <div
            ref={(el) => {
                ref.current = el;
                if (innerRef) innerRef.current = el;
            }}
            className={`kvp-rd nb-neben-stelle${className ? ` ${className}` : ""}`}
            style={{
                width: breite,
                left: platz?.left ?? -9999,
                top: platz?.top ?? -9999,
                // `opacity` statt `visibility: hidden`: Ein unsichtbares Element
                // nimmt keinen Fokus — das `autoFocus` des Schreibfelds liefe
                // beim ersten Zeichnen ins Leere, und die Tasten landeten im
                // Notizfeld darunter.
                opacity: platz ? 1 : 0,
                minHeight: 0,
            }}
            {...(role ? { role } : {})}
            {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
            {...(onMouseDown ? { onMouseDown } : {})}
        >
            {children}
        </div>,
        document.body,
    );
};
